import { useState } from 'react';
import {
  FormLayout,
  FormLayoutGroup,
  InlineStack,
  Modal,
  ModalBody,
  Paragraph,
  SegmentedControl,
  Select,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import type { PushPlatform } from '@/features/push-credentials';
import type { App } from '@/services/db/app/entity';
import { appSelectOptions, platformOptionsFor, resolvePlatform, resolveSelectedApp } from '../utils/test-app';
import { AppPushPreview } from './app-push-preview';
import { AppSendTest } from './app-send-test';

export type TestAppModalData = {
  workspaceId: number;
  apps: App[];
  /** Passed in rather than read here: a managed modal renders outside the auth provider. */
  isAccountUnconfirmed: boolean;
};

export function TestAppModal({ data, onClose, modalState }: PlumaManagedModalProps<TestAppModalData>) {
  const { workspaceId, apps, isAccountUnconfirmed } = data;
  const [pickedAppId, setPickedAppId] = useState<number | null>(null);
  const [pickedPlatform, setPickedPlatform] = useState<PushPlatform | null>(null);

  const app = resolveSelectedApp(apps, pickedAppId);
  const platform = resolvePlatform(app, pickedPlatform);

  const chooseApp = (value: string | null) => {
    setPickedAppId(value == null ? null : Number(value));
    // The new app has its own platforms, so any earlier choice no longer applies.
    setPickedPlatform(null);
  };

  return (
    <Modal state={modalState} title="Send a test" onClose={() => onClose()} shouldShowCloseButton={true}>
      <ModalBody>
        <FormLayout>
          <FormLayoutGroup>
            <Select
              label="App"
              value={app == null ? null : String(app.id)}
              options={appSelectOptions(apps)}
              onChange={chooseApp}
            />
            <SegmentedControl
              label="Platform"
              name="test-app-platform"
              value={platform}
              options={platformOptionsFor(app)}
              isFullWidth={true}
              onChange={(value) => setPickedPlatform(value === 'ios' ? 'ios' : 'android')}
            />
          </FormLayoutGroup>

          {app == null ? null : (
            <AppSendTest
              workspaceId={workspaceId}
              appId={app.id}
              platform={platform}
              hasPlatforms={app.platforms.length > 0}
              isAccountUnconfirmed={isAccountUnconfirmed}
            />
          )}
        </FormLayout>

        <InlineStack alignInline="center" mt="200">
          <Paragraph sizeVariant="xs" variant="secondary" fontStyle="italic">
            Your test push will look like this.
          </Paragraph>
        </InlineStack>

        <AppPushPreview appName={app?.name ?? ''} platform={platform} />
      </ModalBody>
    </Modal>
  );
}
